<template>
  <div class="container-fluid">

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box">
        <div class="loader-3"></div>
      </div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="loadAll()">Reintentar</button>
      </div>
    </div>

    <!-- Contenido -->
    <div v-else-if="welcome != null">

      <!-- Tarjeta de bienvenida (nodo 23) -->
      <div class="row starter-main">
        <div class="col-sm-12 col-lg-12 mt-3" v-for="post in welcome" :key="post.id">
          <div class="card">
            <img
              v-if="post.thumbnail_rendition"
              :src="post.thumbnail_rendition.url"
              :alt="post.thumbnail_rendition.alt"
              class="card-img-top"
              style="object-fit: contain; max-height: 160px; padding: 1rem;"
            />
            <div class="card-header">
              <h5>{{ post.title }}</h5>
            </div>
            <div class="card-body">
              <div class="post-body" v-html="post.body"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Entradas del blog en dos columnas -->
      <div id="blog-list" class="row starter-main">
        <div class="col-sm-6 mb-3" v-for="post in blogPosts" :key="post.id">
          <router-link
            :to="{ name: 'detallePost', params: { id: post.id } }"
            class="text-decoration-none d-block h-100"
          >
            <div
              class="card h-100"
              style="cursor: pointer; transition: box-shadow 0.2s;"
              @mouseenter="e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(16,46,82,0.18)'"
              @mouseleave="e => e.currentTarget.style.boxShadow = ''"
            >
              <div class="card-header p-0" style="overflow: hidden; border-radius: 15px 15px 0 0;">
                <img
                  v-if="post.thumbnail_rendition"
                  :src="post.thumbnail_rendition.full_url"
                  :alt="post.thumbnail_rendition.alt"
                  style="width: 100%; height: 200px; object-fit: cover; display: block;"
                />
              </div>
              <div class="card-body" style="background-color: #09377c;">
                <h5 class="mb-1" style="color: #fff;">{{ post.title }}</h5>
                <p class="small mb-0" style="color: rgba(255,255,255,0.7);">{{ post.date }}</p>
              </div>
              <div class="card-footer" v-if="post.footer">
                <p>Para mayor información <span v-html="post.footer"></span></p>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="totalPages > 1" class="row">
        <div class="col-12 d-flex justify-content-between align-items-center mt-2 mb-4 px-3">

          <span class="text-muted small">
            Mostrando {{ rangoInicio }}–{{ rangoFin }} de {{ totalCount }} entradas
          </span>

          <nav aria-label="Paginación del blog">
            <ul class="pagination pagination-sm mb-0">

              <!-- Anterior -->
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
                  &laquo;
                </button>
              </li>

              <!-- Páginas numéricas -->
              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === currentPage, disabled: page === '…' }"
              >
                <button
                  class="page-link"
                  @click="page !== '…' && goToPage(page)"
                  :disabled="page === '…'"
                >
                  {{ page }}
                </button>
              </li>

              <!-- Siguiente -->
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
                  &raquo;
                </button>
              </li>

            </ul>
          </nav>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";

const PAGE_SIZE = 6;

export default {
  name: "BlogIndice",

  data() {
    return {
      welcome:     null,
      blogPosts:   [],
      totalCount:  0,
      currentPage: 1,
      error:       null,
      loading:     true,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / PAGE_SIZE);
    },
    rangoInicio() {
      return (this.currentPage - 1) * PAGE_SIZE + 1;
    },
    rangoFin() {
      return Math.min(this.currentPage * PAGE_SIZE, this.totalCount);
    },
    // Genera la secuencia de páginas con "…" cuando hay muchas
    visiblePages() {
      const total = this.totalPages;
      const cur   = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      const pages = [];
      if (cur <= 4) {
        pages.push(1, 2, 3, 4, 5, '…', total);
      } else if (cur >= total - 3) {
        pages.push(1, '…', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '…', cur - 1, cur, cur + 1, '…', total);
      }
      return pages;
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    async loadAll() {
      this.loading = true;
      this.error = null;
      try {
        await Promise.all([this.fetchPosts(), this.fetchBlog(1)]);
      } finally {
        this.loading = false;
      }
    },

    async goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
      await this.fetchBlog(page);
      document.getElementById('blog-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    async fetchPosts() {
      try {
        const { data } = await api.get(
          "blog?id=23&fields=intro,body,thumbnail,thumbnail_rendition,footer&order=first_published_at"
        );
        const items = data.items ?? (Array.isArray(data) ? data : [data]);
        this.welcome = await Promise.all(
          items.map(async (post) => ({
            ...post,
            body: await this.resolveBodyImages(post.body),
          }))
        );
      } catch (err) {
        this.error = err;
        console.error("error en fetchPosts", err);
      }
    },

    async fetchBlog(page = 1) {
      const offset = (page - 1) * PAGE_SIZE;
      try {
        const { data } = await api.get("blog", {
          params: {
            child_of: 4,
            fields:   'intro,body,thumbnail,thumbnail_rendition,footer',
            order:    'first_published_at',
            limit:    PAGE_SIZE,
            offset,
          },
        });
        this.blogPosts  = data.items ?? [];
        this.totalCount = data.meta?.total_count ?? this.blogPosts.length;
      } catch (err) {
        this.error = err;
        console.error("error en fetchBlog", err);
      }
    },

    async resolveBodyImages(html) {
      if (!html) return html;
      const BASE = 'https://rverdeadmin.klatus.systems';
      const ids = new Set();
      const re = /<embed[^>]*embedtype="image"[^>]*id="(\d+)"[^>]*/gi;
      let match;
      while ((match = re.exec(html)) !== null) ids.add(match[1]);
      if (!ids.size) return html;

      const imageMap = {};
      await Promise.all([...ids].map(async (id) => {
        try {
          const { data } = await api.get(`images/${id}/`);
          imageMap[id] = data;
        } catch (e) {
          console.warn(`No se pudo cargar imagen ID ${id}`, e);
        }
      }));

      return html.replace(/<embed([^>]*)\/?>(?:<\/embed>)?/gi, (fullMatch, attrs) => {
        if (!/embedtype="image"/i.test(attrs)) return fullMatch;
        const idMatch  = /\bid="(\d+)"/i.exec(attrs);
        const altMatch = /\balt="([^"]*)"/i.exec(attrs);
        const fmtMatch = /\bformat="([^"]*)"/i.exec(attrs);
        if (!idMatch) return fullMatch;
        const img = imageMap[idMatch[1]];
        if (!img) return '';
        const rawUrl = img.meta?.download_url || img.url || '';
        const url    = rawUrl.startsWith('http') ? rawUrl : `${BASE}${rawUrl}`;
        const alt    = altMatch ? altMatch[1] : (img.title || '');
        return `<img src="${url}" alt="${alt}" class="img-fluid d-block mx-auto mb-2" style="max-width: 90%;" />`;
      });
    },
  },
};
</script>